// src/components/exam-player/renderers/PassageMcqRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const Left = React.memo(function Left({
  isRtl,
  renderedPassage,
  slotCount,
  t,
}) {
  return (
    <div className="prose max-w-none max-h-[80vh] overflow-y-scroll">
      <div dir={isRtl ? "rtl" : "ltr"} className="prose max-w-none">
        {renderedPassage}
      </div>
      {slotCount === 0 && (
        <div className="mt-3 text-xs text-amber-600">
          {t("passage_mcq.no_slots_hint", "No slots ([]) found in passage.")}
        </div>
      )}
    </div>
  );
});

const Right = React.memo(function Right({ stemHtml }) {
  return (
    <div className="prose max-w-none">
      {stemHtml ? (
        <div
          className="prose max-w-none text-gray-800"
          dangerouslySetInnerHTML={{ __html: stemHtml }}
        />
      ) : null}
    </div>
  );
});

const TwoPane = React.memo(function TwoPane({ layout, children }) {
  const containerClass =
    layout === "left-right" || layout === "right-left"
      ? "grid grid-cols-1 md:grid-cols-2 gap-6"
      : "space-y-6";

  if (layout === "left-right" || layout === "top-bottom") {
    return <div className={containerClass}>{children}</div>;
  }
  if (layout === "right-left" || layout === "bottom-top") {
    // جابه‌جایی ترتیب کودکان
    const [a, b] = React.Children.toArray(children);
    return <div className={containerClass}>{[b, a]}</div>;
  }
  return <div className={containerClass}>{children}</div>;
});

/**
 * Answer shape: { slot: number }  // 1-based
 */
export default function PassageMcqRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem = false,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const markerText = body?.markerText || "[]";
  const layout = body?.layout || "left-right";

  const passageHtml = body?.text?.fa?.trim() || body?.text?.en?.trim() || "";
  const sentenceHtml =
    body?.sentence?.fa?.trim() || body?.sentence?.en?.trim() || "";
  const stemHtml = item?.stem?.en || item?.stem?.fa || "";


  // initial
  const initialSlot = useMemo(() => {
    if (!initialAnswers) return null;
    if (typeof initialAnswers?.slot === "number") return initialAnswers.slot;
    if (typeof initialAnswers === "number") return initialAnswers;
    return null;
  }, [initialAnswers]);

  const [selectedSlot, setSelectedSlot] = useState(initialSlot);
  useEffect(() => setSelectedSlot(initialSlot), [initialSlot]);

  // count markers
  const slotCount = useMemo(() => {
    if (!passageHtml || !markerText) return 0;
    const re = new RegExp(
      markerText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "g"
    );
    return (passageHtml.match(re) || []).length;
  }, [passageHtml, markerText]);

  const chooseSlot = (n) => {
    setSelectedSlot(n);
    onAnswerChange?.({ slot: n });
  };

  // Build passage with clickable slots and inline sentence when selected
  const renderedPassage = useMemo(() => {
    if (!passageHtml) return "";

    let idx = 0;
    const safeMarker = markerText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(safeMarker, "g");
    const PH = "__PASSAGE_SLOT__UNIQUE__";
    const htmlWithPH = passageHtml.replace(re, () => {
      idx += 1;
      return `${PH}${idx}__`;
    });

    const chunks = htmlWithPH.split(new RegExp(`${PH}(\\d+)__`, "g"));
    const nodes = [];
    for (let i = 0; i < chunks.length; i++) {
      if (i % 2 === 0) {
        const html = chunks[i];
        if (html) {
          nodes.push(
            <span key={`t-${i}`} dangerouslySetInnerHTML={{ __html: html }} />
          );
        }
      } else {
        const slotNum = Number(chunks[i]);
        const active = selectedSlot === slotNum;
        const slotContent = active
          ? `<span class="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">${
              sentenceHtml ||
              t("passage_mcq.sentence_placeholder", "(sentence)")
            }</span>`
          : `<span class="inline-flex items-center justify-center align-middle w-4 h-4 rounded bg-teal-600" title="${t(
              "passage_mcq.click_to_insert",
              "Click to insert"
            )}"></span>`;

        nodes.push(
          <span
            key={`s-${slotNum}`}
            className={`cursor-pointer inline align-middle ${
              active ? "ring-2 ring-amber-300 rounded-sm" : ""
            }`}
            onClick={() => chooseSlot(slotNum)}
            dangerouslySetInnerHTML={{ __html: slotContent }}
          />
        );
      }
    }
    return nodes;
  }, [passageHtml, markerText, selectedSlot, sentenceHtml, t]);

  // Layout
  const containerClass =
    layout === "left-right" || layout === "right-left"
      ? "grid grid-cols-1 md:grid-cols-2 gap-6"
      : "space-y-6";

  return (
    <div className="rounded-2xl border bg-white p-5">
      {/* نکته: این شرط ظاهراً برعکسه. شاید می‌خواستی !!passageHtml باشه */}
      {!passageHtml && (
        <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
          {slotCount > 0 && (
            <div>
              {t("passage_mcq.slot_count", {
                defaultValue: "Slots: {{n}}",
                n: slotCount,
              })}
            </div>
          )}
        </div>
      )}

      <TwoPane layout={layout}>
        <Left
          isRtl={isRtl}
          renderedPassage={renderedPassage}
          slotCount={slotCount}
          t={t}
        />
        <Right stemHtml={stemHtml} />
      </TwoPane>
    </div>
  );
}
